import React, { useEffect, useState, useRef } from "react";
import { HeroSection } from "../../components/HeroSection";
import { ServiceSection } from "../../components/ServiceSection";
import { Reviews } from "../../components/Reviews";
import "./style.css";
import { ContactForm } from "../../components/ContactForm";
import { Footer } from "../../components/Footer";
import { SEOHead } from "../../components/SEOHead";
import { MotionConfig, motion, useInView } from "framer-motion";
import { useLocation } from "react-router-dom";
import {
  CONTACT_SECTION_ID,
  scrollToSectionById,
  shouldScrollToContactFromLocation,
} from "../../utils/contactNavigation";

/* ═══════════════════════════════════════════════════════════ */
/*  HELPERS                                                    */
/* ═══════════════════════════════════════════════════════════ */

const FadeIn: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 12 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.4, 0, 0.2, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
/*  DATA                                                       */
/* ═══════════════════════════════════════════════════════════ */

const insightSteps = [
  {
    title: "Capture the idea",
    desc: "Start privately with a rough idea. Nothing needs to be polished or public.",
  },
  {
    title: "Research with evidence",
    desc: "Request research and review the evidence behind it before you commit time or money.",
  },
  {
    title: "Record the decision",
    desc: "Keep versioned briefs and the reasoning for each decision, so the next step is clear.",
  },
];

const offers = [
  {
    title: "Analyze",
    desc: "We study your idea or existing business to see what works, what does not and what to test.",
  },
  {
    title: "Build",
    desc: "We turn that evidence into the right app, website or tool, not a longer feature list.",
  },
  {
    title: "Grow",
    desc: "We run campaigns and experiments, then measure what actually improves.",
  },
];

const processSteps = [
  { step: "01", title: "Analyze", desc: "Goals, audience and the evidence behind the idea." },
  { step: "02", title: "Design", desc: "Prototypes tested with real users." },
  { step: "03", title: "Build", desc: "Agile sprints with live demos." },
  { step: "04", title: "Measure", desc: "Launch, monitor and improve against the goal." },
];

const faqList = [
  {
    q: "What is Etlyn Insight?",
    a: "Insight is our flagship product. It brings ideas, research and decisions into one workflow so you can give an idea structure before you build it.",
  },
  {
    q: "Can I use Insight today?",
    a: "Insight is in early pilot. Contact us with what you are testing and we will tell you plainly what is available now.",
  },
  {
    q: "How does your development process work?",
    a: "Analysis → Design → Agile sprints with live demos every 2 weeks → Launch with ongoing monitoring.",
  },
  {
    q: "How much does it cost?",
    a: "Pricing depends on scope. Fixed-price or time-and-materials — we provide a detailed estimate upfront with no hidden fees.",
  },
  {
    q: "Do you build for both iOS and Android?",
    a: "Yes. Cross-platform apps from a single codebase that run natively on both platforms.",
  },
  {
    q: "Do you offer support after launch?",
    a: "Yes — maintenance plans that include monitoring, bug fixes, security patches and feature enhancements.",
  },
];

/* ═══════════════════════════════════════════════════════════ */
/*  SUB-COMPONENTS                                             */
/* ═══════════════════════════════════════════════════════════ */

/* ── FAQ ─────────────────────────────────────────────────── */
function FaqItem({ q, a, id }: { q: string; a: string; id: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#DFDFE0]/60 last:border-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-4 text-left text-sm font-medium text-[#333D51] transition-colors duration-300 hover:text-[#0D2B7E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#50BCFF]"
      >
        <span>{q}</span>
        <svg
          className={`h-4 w-4 shrink-0 text-[#808692] transition-transform duration-300 ease-in-out ${
            open ? "rotate-45" : ""
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4.5v15m7.5-7.5h-15"
          />
        </svg>
      </button>
      <div
        id={`${id}-panel`}
        role="region"
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="pb-4 pr-8 text-sm leading-relaxed text-[#666D7C]">{a}</p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  HOME PAGE                                                  */
/* ═══════════════════════════════════════════════════════════ */

export const HomePage = () => {
  const location = useLocation();
  const [device, setDevice] = useState<"web" | "mob">("web");
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");

    const updateDevice = (event: MediaQueryList | MediaQueryListEvent) => {
      setDevice(event.matches ? "mob" : "web");
    };

    updateDevice(mediaQuery);
    mediaQuery.addEventListener("change", updateDevice);

    return () => {
      mediaQuery.removeEventListener("change", updateDevice);
    };
  }, []);
  const isMob = device === "mob";

  useEffect(() => {
    if (!shouldScrollToContactFromLocation(location.hash, location.search))
      return;

    let attempts = 0;
    const maxAttempts = 10;

    const tryScroll = () => {
      const didScroll = scrollToSectionById(CONTACT_SECTION_ID, "smooth");
      if (didScroll || attempts >= maxAttempts) return;

      attempts += 1;
      window.setTimeout(tryScroll, 120);
    };

    const timer = window.setTimeout(tryScroll, 80);
    return () => window.clearTimeout(timer);
  }, [location.hash, location.search, device]);

  return (
    <MotionConfig reducedMotion="user">
      <div
        className="home-page relative w-full max-w-[1280px] mx-auto px-4 mt-[80px] font-['Inter',sans-serif]"
        style={{ minWidth: 0 }}
      >
        <SEOHead
          useSiteDefaults
          title="Etlyn Insight, Business Analysis and Software Development"
          description="Etlyn helps businesses and individuals grow through software. Explore Etlyn Insight and our business analysis, app, website and growth services."
          path="/"
          keywords="software development, web application development, custom software, CMS platform, business growth, mid-size business technology"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "Etlyn",
            url: "https://www.etlyn.com",
            description:
              "Product development company helping businesses and individuals grow through business analysis and software solutions.",
            areaServed: "Worldwide",
            serviceType: [
              "Software Development",
              "Web Application Development",
              "Mobile App Development",
              "CMS Development",
            ],
          }}
        />

        {/* ─── 1. HERO ─────────────────────────────────────── */}
        <HeroSection className="w-full max-w-full" />

        {/* ─── 2. INSIGHT ──────────────────────────────────── */}
        <section aria-labelledby="insight-title" className="mt-24 mb-24">
          <FadeIn>
            <div className="max-w-2xl">
              <p className="text-sm font-medium text-[#666D7C]">Our flagship product</p>
              <h2 id="insight-title" className="mt-2 text-2xl font-semibold tracking-tight text-[#000C25] sm:text-3xl">
                A clear next step for every idea
              </h2>
              <p className="mt-4 text-[#333D51] leading-relaxed">
                Etlyn Insight gives an idea structure before you build. Ideas, research and
                decisions live in one workflow, so you know what you are testing and why.
              </p>
            </div>
          </FadeIn>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {insightSteps.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-[#DFDFE0] bg-white p-6 transition-colors duration-300 ease-in-out hover:border-[#0D2B7E]/40">
                  <span className="text-xs font-semibold text-[#0D2B7E]">0{i + 1}</span>
                  <h3 className="mt-3 text-base font-semibold text-[#000C25]">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#666D7C]">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <button
                type="button"
                data-analytics-target="contact"
                onClick={() => scrollToSectionById(CONTACT_SECTION_ID)}
                className="rounded-[10px] bg-[#0D2B7E] px-5 py-3 text-[15px] font-semibold text-white transition-colors duration-300 ease-in-out hover:bg-[#0A2266] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#50BCFF]"
              >
                Discuss an Insight pilot
              </button>
              <p className="text-sm text-[#666D7C]">
                We are developing Insight with early users.
              </p>
            </div>
          </FadeIn>
        </section>

        {/* ─── 2b. OFFERS ──────────────────────────────────── */}
        <section aria-labelledby="offers-title" className="mb-24">
          <FadeIn>
            <div className="max-w-2xl">
              <h2 id="offers-title" className="text-2xl font-semibold tracking-tight text-[#000C25] sm:text-3xl">
                And when you need a team
              </h2>
              <p className="mt-4 text-[#333D51] leading-relaxed">
                Etlyn analyzes ideas and businesses, builds software and tests growth
                opportunities. Work with us on one step or all three.
              </p>
            </div>
          </FadeIn>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {offers.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-[#DFDFE0] bg-white p-6 transition-colors duration-300 ease-in-out hover:border-[#0D2B7E]/40">
                  <h3 className="text-base font-semibold text-[#000C25]">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#666D7C]">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </section>

        {/* ─── 3. SERVICES (CMS-driven) ────────────────────── */}
        <ServiceSection device={device} />

        {/* ─── 4. WHY US ───────────────────────────────────── */}
        <section className="mb-24">
          <FadeIn>
            <div className="rounded-3xl bg-gradient-to-br from-[#0D2B7E] to-[#25408B] p-8 sm:p-12">
              <div className="max-w-xl">
                <h2 className="mb-5 text-xl font-semibold leading-snug tracking-tight text-white sm:text-2xl">
                  We engineer growth, not just software
                </h2>
                <ul className="mb-7 space-y-3">
                  {[
                    "Every feature tied to a business outcome",
                    "Full source-code ownership — zero lock-in",
                    "A detailed estimate before any work starts",
                    "Live demos every two weeks",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-sm leading-relaxed text-white/80"
                    >
                      <svg
                        className="mt-0.5 h-4 w-4 shrink-0 text-[#28E454]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m4.5 12.75 6 6 9-13.5"
                        />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  data-analytics-target="contact"
                  onClick={() => scrollToSectionById(CONTACT_SECTION_ID)}
                  className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-[#0D2B7E] transition-colors duration-300 ease-in-out hover:bg-[#EEF1FB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#50BCFF]"
                >
                  Start a project
                </button>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* ─── 5. PROCESS ──────────────────────────────────── */}
        <section id="approach" aria-labelledby="approach-title" className="mb-24 scroll-mt-24">
          <FadeIn>
            <div className="mb-10 max-w-2xl">
              <h2 id="approach-title" className="text-2xl font-semibold tracking-tight text-[#000C25] sm:text-3xl">
                From idea to measured result
              </h2>
              <p className="mt-4 text-[#333D51] leading-relaxed">
                Each step produces something you can review, so scope follows evidence.
              </p>
            </div>
          </FadeIn>

          <ol className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {processSteps.map((s, i) => (
              <li key={s.step}>
                <FadeIn delay={i * 0.08} className="h-full">
                  <div className="h-full rounded-2xl border border-[#DFDFE0] bg-white p-6 transition-colors duration-300 ease-in-out hover:border-[#0D2B7E]/40">
                    <span className="text-xs font-semibold text-[#0D2B7E]">{s.step}</span>
                    <h3 className="mt-3 text-base font-semibold text-[#000C25]">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#666D7C]">{s.desc}</p>
                  </div>
                </FadeIn>
              </li>
            ))}
          </ol>
        </section>

        {/* ─── 6. REVIEWS ──────────────────────────────────── */}
        <Reviews isMob={isMob} />

        {/* ─── 7. FAQ ──────────────────────────────────────── */}
        <section className="mb-24 mt-24">
          <FadeIn>
            <div className="text-center mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-[#333D51] tracking-tight">
                FAQ
              </h2>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="mx-auto max-w-2xl rounded-2xl bg-white/80 backdrop-blur-sm p-6 sm:p-8 ring-1 ring-[#DFDFE0]/60">
              {faqList.map((item, i) => (
                <FaqItem key={item.q} id={`faq-${i}`} q={item.q} a={item.a} />
              ))}
            </div>
          </FadeIn>
        </section>

        {/* ─── 8. CONTACT FORM ────────────────────────────── */}
        <ContactForm isMob={isMob} />

        {/* ─── 9. FOOTER ──────────────────────────────────── */}
        <Footer isMob={isMob} />
      </div>
    </MotionConfig>
  );
};
